/**
 * Questionnaire beta — modale de feedback, ouverte via le store
 * (`feedbackOpen`) depuis le bandeau « beta » (en jeu) ou le CTA de fin.
 *
 * Le joueur note son ressenti sur 6 dimensions (Likert 1..5). L'intro
 * l'assume : le jeu est en beta, il fait partie des premiers joueurs, et
 * son avis sert à l'améliorer. Réponses partielles acceptées ; envoi
 * fire-and-forget vers la table dédiée (POST /api/feedback).
 */

import { useState } from "react";
import { useGame } from "../store/useGame";
import { submitFeedback, type SurveyRatings } from "../engine/tracking";
import { SUPPORT_URL } from "../support";

const GIVEN_KEY = "mp.feedbackGiven";

interface Question {
  key: keyof SurveyRatings;
  statement: string;
}

const QUESTIONS: Question[] = [
  { key: "interest", statement: "Le jeu m'a intéressé" },
  { key: "themes", statement: "Les thèmes abordés sont pertinents" },
  { key: "mechanics", statement: "J'ai compris les mécaniques de jeu" },
  { key: "objectives", statement: "Le but de mon mandat était clair" },
  { key: "satisfaction", statement: "Je me suis amusé" },
  { key: "replay", statement: "J'ai envie d'y rejouer" },
];

const SCALE = [1, 2, 3, 4, 5];

export function FeedbackModal() {
  const open = useGame((s) => s.feedbackOpen);
  const close = useGame((s) => s.closeFeedback);
  const sessionId = useGame((s) => s.sessionId);

  const [ratings, setRatings] = useState<SurveyRatings>({});
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const answered = Object.keys(ratings).length;

  const send = () => {
    if (answered === 0 || !sessionId) return;
    submitFeedback(sessionId, ratings);
    try {
      localStorage.setItem(GIVEN_KEY, "1");
    } catch {
      /* ignore */
    }
    setSent(true);
  };

  const dismiss = () => {
    close();
    // Reset pour une éventuelle réouverture propre.
    setRatings({});
    setSent(false);
  };

  return (
    <div
      className="feedback-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={dismiss}
    >
      <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="feedback-close"
          onClick={dismiss}
          aria-label="Fermer"
        >
          ×
        </button>

        {sent ? (
          <div className="feedback-done">
            <div className="feedback-kicker">MERCI ●</div>
            <h2 className="feedback-modal-title">Votre avis est enregistré.</h2>
            <p className="feedback-intro">
              Merci d'aider à façonner le jeu. Vous pouvez rouvrir ce
              questionnaire à tout moment depuis le bandeau « beta ».
            </p>
            <button type="button" className="feedback-submit" onClick={dismiss}>
              ━━ FERMER ━━
            </button>
            {SUPPORT_URL && (
              <a
                className="feedback-support"
                href={SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                ♥ Soutenir le projet
              </a>
            )}
          </div>
        ) : (
          <>
            <div className="feedback-kicker">● VERSION BETA · VOTRE AVIS</div>
            <h2 className="feedback-modal-title">
              Vous faites partie des tout premiers joueurs.
            </h2>
            <p className="feedback-intro">
              Le jeu est en construction et on cherche à l'améliorer. Une minute
              pour dire ce que vous en avez pensé&nbsp;? Votre retour pèse
              vraiment.
            </p>

            <p className="feedback-legend">
              <span>1 — Pas du tout d'accord</span>
              <span>5 — Tout à fait d'accord</span>
            </p>

            <ul className="feedback-questions">
              {QUESTIONS.map((q) => (
                <li key={q.key} className="feedback-question">
                  <span className="feedback-question-label">{q.statement}</span>
                  <span
                    className="feedback-scale"
                    role="radiogroup"
                    aria-label={q.statement}
                  >
                    {SCALE.map((v) => {
                      const selected = ratings[q.key] === v;
                      return (
                        <button
                          key={v}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          aria-label={`${v} sur 5`}
                          className="feedback-pip"
                          data-selected={selected}
                          onClick={() =>
                            setRatings((r) => ({ ...r, [q.key]: v }))
                          }
                        >
                          {v}
                        </button>
                      );
                    })}
                  </span>
                </li>
              ))}
            </ul>

            <div className="feedback-survey-actions">
              <button
                type="button"
                className="feedback-cancel"
                onClick={dismiss}
              >
                Plus tard
              </button>
              <button
                type="button"
                className="feedback-submit"
                onClick={send}
                disabled={answered === 0}
              >
                ━━ ENVOYER ━━
              </button>
            </div>
            {SUPPORT_URL && (
              <a
                className="feedback-support"
                href={SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                ♥ Soutenir le projet
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
}
